import dayjs from 'dayjs';

import i18n from '@/i18n';
import {graphqlClient} from '@/services/graphqlClient';
import type {GetWorkScheduleMonthForExportQuery} from '@/services/graphqlHooks';
import {
  useGetUsersForExportQuery,
  useGetWorkScheduleMonthForExportQuery,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';
import {TIME_FORMATS} from '@/utils/timeFormats';

import DateRangeOptionsFields from '../../components/export-options/DateRangeOptionsFields';
import type {ExportDefinition} from '../../types/exportTypes';
import type {DateRangeOptions} from '../exportOptions';
import {
  dateRangeOptionsSchema,
  describeDateRange,
  formatExportDateTime,
  formatPersonName,
  getDateRangeFileName,
  isWithinDateRange,
} from '../exportOptions';

type ShiftExportRecord =
  GetWorkScheduleMonthForExportQuery['getWorkScheduleMonth'][number] & {
    employeeName: string;
  };

const getWeekDefaultOptions = (
  searchParams: URLSearchParams
): DateRangeOptions => {
  const weekStart = dayjs(searchParams.get('week') ?? undefined).startOf(
    'isoWeek'
  );

  return {
    startDate: weekStart.format(TIME_FORMATS.awsDate),
    endDate: weekStart.endOf('isoWeek').format(TIME_FORMATS.awsDate),
  };
};

const getMonthStarts = ({startDate, endDate}: DateRangeOptions) => {
  const firstMonth = dayjs(startDate).startOf('month');
  const monthCount = dayjs(endDate).startOf('month').diff(firstMonth, 'month');

  return Array.from({length: monthCount + 1}, (_, index) =>
    firstMonth.add(index, 'month').format(TIME_FORMATS.awsDate)
  );
};

const fetchMonthShifts = async (monthStart: string) => {
  const variables = {monthStart};
  const {getWorkScheduleMonth} = await queryClient.fetchQuery({
    queryKey: useGetWorkScheduleMonthForExportQuery.getKey(variables),
    queryFn: useGetWorkScheduleMonthForExportQuery.fetcher(
      graphqlClient,
      variables
    ),
  });

  return getWorkScheduleMonth;
};

export const shiftsExport: ExportDefinition<
  DateRangeOptions,
  ShiftExportRecord
> = {
  id: 'shifts',
  label: i18n.t('Work schedule'),
  permission: 'EXPORT_WORK_SCHEDULE',
  entitlement: 'WORK_SCHEDULE',
  optionsSchema: dateRangeOptionsSchema,
  OptionsFields: DateRangeOptionsFields,
  getDefaultOptions: getWeekDefaultOptions,
  load: async options => {
    const [months, {getAllUsers}] = await Promise.all([
      Promise.all(getMonthStarts(options).map(fetchMonthShifts)),
      queryClient.fetchQuery({
        queryKey: useGetUsersForExportQuery.getKey(),
        queryFn: useGetUsersForExportQuery.fetcher(graphqlClient),
      }),
    ]);
    const users = new Map(getAllUsers.map(user => [user.id, user.name]));

    return months
      .flat()
      .filter(shift => isWithinDateRange(shift.startsAt, options))
      .map(shift => ({
        ...shift,
        employeeName: formatPersonName(users.get(shift.userId)) || shift.userId,
      }))
      .sort((shiftA, shiftB) => dayjs(shiftA.startsAt).diff(shiftB.startsAt));
  },
  columns: [
    {
      header: i18n.t('Employee'),
      filterable: true,
      value: shift => shift.employeeName,
    },
    {
      header: i18n.t('Job role'),
      filterable: true,
      value: shift => shift.jobRoleName,
    },
    {
      header: i18n.t('Starts at'),
      value: shift => formatExportDateTime(shift.startsAt),
    },
    {
      header: i18n.t('Ends at'),
      value: shift => formatExportDateTime(shift.endsAt),
    },
  ],
  describeOptions: describeDateRange,
  getFileName: options => getDateRangeFileName('shifts', options),
};
